import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  Mail,
  Route,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  TrendingUp,
} from 'lucide-react'
import { useMemo, useRef, useState } from 'react'
import { useRevealMotion } from '../motion/useRevealMotion'
import '../styles/direct-delivery-case-v2.css'

type ScenarioId = 'downside' | 'working' | 'fuller'

interface DeliveryScenario {
  readonly id: ScenarioId
  readonly label: string
  readonly shortLabel: string
  readonly description: string
  readonly commissionBps: number
  readonly averageOrderCents: number
  readonly completedDropsPerRun: number
  readonly runsPerWeek: number
  readonly allInRunCostCents: number
}

interface ScenarioResult {
  readonly marketplaceFeeCents: number
  readonly processingCents: number
  readonly routeCostPerDropCents: number
  readonly netRouteCostCents: number
  readonly directVariableCostCents: number
  readonly differencePerOrderCents: number
  readonly pilotOrderCount: number
  readonly pilotDifferenceCents: number
  readonly annualOrderCount: number
  readonly annualDifferenceCents: number
  readonly breakEvenDropsPerRun: number | null
  readonly breakEvenAverageOrderCents: number | null
}

const DELIVERY_FEE_CENTS = 500
const PROCESSING_RATE_BPS = 175
const PROCESSING_FIXED_CENTS = 30
const FULFILMENT_CENTS = 350
const SUPPORT_CENTS = 100
const DIRECT_MARKETING_CENTS = 100

const scenarios: readonly DeliveryScenario[] = [
  {
    id: 'downside',
    label: 'Downside check',
    shortLabel: 'Downside',
    description:
      'A lower marketplace rate and a lightly filled run. Direct delivery costs more in this case.',
    commissionBps: 1500,
    averageOrderCents: 3500,
    completedDropsPerRun: 12,
    runsPerWeek: 2,
    allInRunCostCents: 14400,
  },
  {
    id: 'working',
    label: 'Working pilot',
    shortLabel: 'Working',
    description:
      'A mid-tier marketplace rate and a route that is close to the break-even density.',
    commissionBps: 2500,
    averageOrderCents: 4000,
    completedDropsPerRun: 18,
    runsPerWeek: 2,
    allInRunCostCents: 15300,
  },
  {
    id: 'fuller',
    label: 'Fuller route',
    shortLabel: 'Fuller route',
    description:
      'A higher marketplace rate, stronger average order and a denser planned run.',
    commissionBps: 3000,
    averageOrderCents: 4500,
    completedDropsPerRun: 24,
    runsPerWeek: 2,
    allInRunCostCents: 16800,
  },
] as const

function percentageOfCents(cents: number, basisPoints: number): number {
  return Math.round((cents * basisPoints) / 10000)
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
  }).format(cents / 100)
}

function formatSignedCurrency(cents: number): string {
  if (cents > 0) return `+${formatCurrency(cents)}`
  return formatCurrency(cents)
}

function calculateDirectVariableCost(
  averageOrderCents: number,
  routeCostPerDropCents: number,
): {
  processingCents: number
  netRouteCostCents: number
  directVariableCostCents: number
} {
  const amountChargedCents = averageOrderCents + DELIVERY_FEE_CENTS
  const processingCents =
    percentageOfCents(amountChargedCents, PROCESSING_RATE_BPS) +
    PROCESSING_FIXED_CENTS
  const netRouteCostCents = Math.max(
    routeCostPerDropCents - DELIVERY_FEE_CENTS,
    0,
  )

  return {
    processingCents,
    netRouteCostCents,
    directVariableCostCents:
      processingCents +
      FULFILMENT_CENTS +
      SUPPORT_CENTS +
      DIRECT_MARKETING_CENTS +
      netRouteCostCents,
  }
}

function findBreakEvenAverageOrderCents(
  scenario: DeliveryScenario,
  routeCostPerDropCents: number,
): number | null {
  for (
    let averageOrderCents = 1000;
    averageOrderCents <= 20000;
    averageOrderCents += 1
  ) {
    const marketplaceFeeCents = percentageOfCents(
      averageOrderCents,
      scenario.commissionBps,
    )
    const { directVariableCostCents } = calculateDirectVariableCost(
      averageOrderCents,
      routeCostPerDropCents,
    )

    if (marketplaceFeeCents >= directVariableCostCents) {
      return averageOrderCents
    }
  }

  return null
}

function calculateScenario(scenario: DeliveryScenario): ScenarioResult {
  const routeCostPerDropCents = Math.ceil(
    scenario.allInRunCostCents / scenario.completedDropsPerRun,
  )
  const marketplaceFeeCents = percentageOfCents(
    scenario.averageOrderCents,
    scenario.commissionBps,
  )
  const {
    processingCents,
    netRouteCostCents,
    directVariableCostCents,
  } = calculateDirectVariableCost(
    scenario.averageOrderCents,
    routeCostPerDropCents,
  )
  const differencePerOrderCents =
    marketplaceFeeCents - directVariableCostCents
  const ordersPerWeek =
    scenario.completedDropsPerRun * scenario.runsPerWeek
  const pilotOrderCount = ordersPerWeek * 4
  const annualOrderCount = ordersPerWeek * 52
  const nonRouteDirectCostCents =
    processingCents +
    FULFILMENT_CENTS +
    SUPPORT_CENTS +
    DIRECT_MARKETING_CENTS
  const maximumRouteCostPerDropCents =
    DELIVERY_FEE_CENTS +
    marketplaceFeeCents -
    nonRouteDirectCostCents
  const breakEvenDropsPerRun =
    maximumRouteCostPerDropCents > 0
      ? Math.ceil(
          scenario.allInRunCostCents / maximumRouteCostPerDropCents,
        )
      : null

  return {
    marketplaceFeeCents,
    processingCents,
    routeCostPerDropCents,
    netRouteCostCents,
    directVariableCostCents,
    differencePerOrderCents,
    pilotOrderCount,
    pilotDifferenceCents: differencePerOrderCents * pilotOrderCount,
    annualOrderCount,
    annualDifferenceCents: differencePerOrderCents * annualOrderCount,
    breakEvenDropsPerRun,
    breakEvenAverageOrderCents: findBreakEvenAverageOrderCents(
      scenario,
      routeCostPerDropCents,
    ),
  }
}

function ResultLabel({ cents }: { readonly cents: number }) {
  if (cents > 0) return <>Potentially retained</>
  if (cents < 0) return <>Additional direct cost</>
  return <>Break-even</>
}

export function DirectDeliveryCasePage() {
  const appRef = useRef<HTMLDivElement>(null)
  const [scenarioId, setScenarioId] = useState<ScenarioId>('working')
  const scenario =
    scenarios.find((item) => item.id === scenarioId) ?? scenarios[1]
  const result = useMemo(() => calculateScenario(scenario), [scenario])
  const resultTone =
    result.differencePerOrderCents > 0
      ? 'is-positive'
      : result.differencePerOrderCents < 0
        ? 'is-negative'
        : 'is-neutral'

  useRevealMotion(appRef)

  return (
    <div className="app case-page" ref={appRef}>
      <header className="case-header">
        <a
          className="case-logo"
          href="/"
          aria-label="Back to the Penny Wise I.T proposal introduction"
        >
          <img
            src="/brand/sub-zero-logo-primary.png"
            alt="Sub Zero Treats"
          />
        </a>
        <p>Direct-repeat pilot proposal</p>
        <nav aria-label="Proposal navigation">
          <a className="case-header__proposal" href="/">
            <ArrowLeft aria-hidden="true" />
            Proposal
          </a>
          <a href="#the-numbers">Numbers</a>
          <a href="#four-week-pilot">4-week pilot</a>
          <a className="case-header__demo" href="/experience">
            Shopper demo
            <ArrowRight aria-hidden="true" />
          </a>
        </nav>
      </header>

      <main id="top">
        <section
          className="case-thesis"
          data-case-section="thesis"
          aria-labelledby="case-thesis-title"
        >
          <div className="case-thesis__copy reveal">
            <p className="case-kicker">A direct-repeat pilot for Sub Zero Treats</p>
            <h1 id="case-thesis-title">
              Let the apps win the first order.
              <span>Let Sub Zero own the next.</span>
            </h1>
            <p className="case-thesis__summary">
              A proposed four-week service that groups repeat orders into
              scheduled local runs, pairs them with a personalised Bucket
              experience and exposes every cost before launch.
            </p>
            <div className="case-thesis__actions">
              <a className="case-action case-action--raspberry" href="#the-numbers">
                Compare the pilot scenarios
                <ArrowDown aria-hidden="true" />
              </a>
              <a className="case-action case-action--ghost" href="/experience">
                Experience the shopper demo
                <ShoppingBag aria-hidden="true" />
              </a>
            </div>
            <aside className="case-thesis__personal">
              <span>The business underneath the model</span>
              <strong>
                A family-built brand should own more of its repeat-customer
                relationship.
              </strong>
              <p>
                This page keeps the focus on the operating decision: whether
                direct repeat demand can become an asset Sub Zero controls
                without changing what customers already value.
              </p>
            </aside>
            <p className="case-thesis__guardrail">
              Illustrative proposal only. Actual contracts, route costs,
              service areas, run days and capacity must be confirmed by Sub
              Zero Treats. No delivery zone or scheduled run shown in this MVP
              is currently available or committed.
            </p>
          </div>

          <div className="case-thesis__visual reveal" aria-hidden="true">
            <picture className="case-thesis__picture">
              <source
                media="(max-width: 700px)"
                srcSet="/media/higgsfield/sweet-impact-mobile.avif?v=20260801-baked"
              />
              <img
                className="case-thesis__media"
                src="/media/higgsfield/sweet-impact-desktop.avif"
                alt=""
              />
            </picture>
            <img
              className="case-thesis__bucket-logo"
              src="/brand/sub-zero-logo-primary.png"
              alt=""
              aria-hidden="true"
            />
            <div className="case-thesis__stamp">
              <Sparkles />
              <span>4-week pilot</span>
            </div>
          </div>
        </section>

        <section
          className="case-operating"
          data-case-section="operating-model"
          aria-labelledby="case-operating-title"
        >
          <div className="case-section-heading reveal">
            <p className="case-kicker">A hybrid channel, by design</p>
            <h2 id="case-operating-title">
              Discovery stays broad. Repeat demand becomes planned.
            </h2>
            <p>
              This is not a proposal to leave DoorDash or Uber Eats. It tests
              whether known repeat demand can move to a service Sub Zero
              controls and can operate reliably.
            </p>
          </div>

          <ol className="case-operating__steps reveal">
            <li>
              <span>01</span>
              <div>
                <strong>Use marketplaces for discovery</strong>
                <p>
                  Keep the reach, delivery network and first-order convenience
                  that the platforms provide.
                </p>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <strong>Earn the direct reorder</strong>
                <p>
                  Give regulars a distinctive reason to return: a guided taste
                  profile, transparent Bucket reveal and weekly option.
                </p>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <strong>Group demand into scheduled runs</strong>
                <p>
                  Set zones, cut-offs and capacity before the vehicle leaves,
                  then compare the real cost with the current contract.
                </p>
              </div>
            </li>
          </ol>

          <div className="case-operating__outcomes reveal">
            <article>
              <Route aria-hidden="true" />
              <strong>Route density</strong>
              <span>Measure completed drops per run, not hopeful capacity.</span>
            </article>
            <article>
              <TrendingUp aria-hidden="true" />
              <strong>Contribution test</strong>
              <span>Show downside, break-even and upside without a profit claim.</span>
            </article>
            <article>
              <ShoppingBag aria-hidden="true" />
              <strong>Owned experience</strong>
              <span>Keep preferences, service and reorder controls together.</span>
            </article>
          </div>
        </section>

        <section
          className="case-financial"
          id="the-numbers"
          data-case-section="financial-case"
          aria-labelledby="case-financial-title"
        >
          <div className="case-financial__heading reveal">
            <p className="case-kicker">Illustrative scenario comparison</p>
            <h2 id="case-financial-title">
              Does a planned route beat the current contract?
            </h2>
            <p>
              The selectors below compare three fixed examples. They are not
              forecasts or editable inputs. A working session would replace
              them with the signed merchant agreement and real route costs.
            </p>
          </div>

          <div
            className="case-scenarios reveal"
            role="group"
            aria-label="Choose an illustrative delivery scenario"
          >
            {scenarios.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={item.id === scenario.id}
                onClick={() => setScenarioId(item.id)}
              >
                <span>{item.shortLabel}</span>
                <small>{item.commissionBps / 100}% marketplace assumption</small>
              </button>
            ))}
          </div>

          <div className="case-economics reveal">
            <article className="case-economics__assumptions">
              <div className="case-economics__card-heading">
                <span>Scenario inputs</span>
                <strong>{scenario.label}</strong>
                <p>{scenario.description}</p>
              </div>
              <dl>
                <div>
                  <dt>Average merchandise order</dt>
                  <dd>{formatCurrency(scenario.averageOrderCents)}</dd>
                </div>
                <div>
                  <dt>Marketplace commission</dt>
                  <dd>{scenario.commissionBps / 100}%</dd>
                </div>
                <div>
                  <dt>Completed drops / run</dt>
                  <dd>{scenario.completedDropsPerRun}</dd>
                </div>
                <div>
                  <dt>Runs / week</dt>
                  <dd>{scenario.runsPerWeek}</dd>
                </div>
                <div>
                  <dt>All-in route cost / run</dt>
                  <dd>{formatCurrency(scenario.allInRunCostCents)}</dd>
                </div>
                <div className="is-unified">
                  <dt>Customer delivery fee</dt>
                  <dd>{formatCurrency(DELIVERY_FEE_CENTS)}</dd>
                  <small>Same proposed fee as the shopper demo.</small>
                </div>
              </dl>
            </article>

            <article className={`case-economics__result ${resultTone}`}>
              <div className="case-economics__card-heading">
                <span>Contribution comparison</span>
                <strong>
                  <ResultLabel cents={result.differencePerOrderCents} />
                </strong>
                <p>
                  Marketplace merchant fee minus the modelled direct variable
                  cost for one migrated repeat order.
                </p>
              </div>
              <div className="case-economics__hero-result">
                <span>Difference / order</span>
                <strong>
                  {formatSignedCurrency(result.differencePerOrderCents)}
                </strong>
              </div>
              <dl>
                <div>
                  <dt>4-week pilot ({result.pilotOrderCount} orders)</dt>
                  <dd>{formatSignedCurrency(result.pilotDifferenceCents)}</dd>
                </div>
                <div>
                  <dt>Annual run-rate ({result.annualOrderCount} orders)</dt>
                  <dd>{formatSignedCurrency(result.annualDifferenceCents)}</dd>
                </div>
                <div>
                  <dt>Break-even route density</dt>
                  <dd>
                    {result.breakEvenDropsPerRun === null
                      ? 'Not reached'
                      : `${result.breakEvenDropsPerRun} drops / run`}
                  </dd>
                </div>
                <div>
                  <dt>Break-even average order</dt>
                  <dd>
                    {result.breakEvenAverageOrderCents === null
                      ? 'Above $200'
                      : formatCurrency(result.breakEvenAverageOrderCents)}
                  </dd>
                </div>
              </dl>
            </article>
          </div>

          <div className="case-economics__detail reveal">
            <article>
              <span>Marketplace side</span>
              <h3>{formatCurrency(result.marketplaceFeeCents)} / order</h3>
              <p>
                Commission applied to the merchandise subtotal. Delivery,
                discovery, support and payment processing may be included in
                the platform service.
              </p>
            </article>
            <article>
              <span>Direct side</span>
              <h3>{formatCurrency(result.directVariableCostCents)} / order</h3>
              <ul>
                <li>
                  <span>Payment processing</span>
                  <strong>{formatCurrency(result.processingCents)}</strong>
                </li>
                <li>
                  <span>Pick, pack and fulfilment</span>
                  <strong>{formatCurrency(FULFILMENT_CENTS)}</strong>
                </li>
                <li>
                  <span>Support + direct marketing</span>
                  <strong>
                    {formatCurrency(SUPPORT_CENTS + DIRECT_MARKETING_CENTS)}
                  </strong>
                </li>
                <li>
                  <span>
                    Net route cost ({formatCurrency(result.routeCostPerDropCents)}
                    {' - '}
                    {formatCurrency(DELIVERY_FEE_CENTS)})
                  </span>
                  <strong>{formatCurrency(result.netRouteCostCents)}</strong>
                </li>
              </ul>
            </article>
          </div>

          <div className="case-economics__sources reveal">
            <div>
              <ShieldCheck aria-hidden="true" />
              <p>
                <strong>Illustration, not a profit guarantee.</strong> Product
                cost is assumed equal across channels and cancels from this
                comparison. GST treatment, tax, insurance, discounts, failed
                deliveries, fixed software, launch cost and owner time are not
                included.
              </p>
            </div>
            <p>
              Processing uses an illustrative 1.75% + $0.30 on the merchandise
              order plus delivery fee. Replace every input with actual
              statements, quotes and route records before deciding.
            </p>
            <div className="case-source-links">
              <a
                href="https://merchants.doordash.com/en-au/marketplace/how-it-works-for-restaurants"
                target="_blank"
                rel="noreferrer"
              >
                DoorDash AU public plans
                <ArrowRight aria-hidden="true" />
              </a>
              <a
                href="https://merchants.ubereats.com/au/en/pricing/"
                target="_blank"
                rel="noreferrer"
              >
                Uber Eats AU public pricing
                <ArrowRight aria-hidden="true" />
              </a>
            </div>
          </div>
        </section>

        <section
          className="case-shopper"
          data-case-section="shopper-experience"
          aria-labelledby="case-shopper-title"
        >
          <div className="case-section-heading reveal">
            <p className="case-kicker">The reason to order direct</p>
            <h2 id="case-shopper-title">
              A customer journey the marketplaces do not own.
            </h2>
            <p>
              The direct service needs more than a cheaper checkout. The
              Create-a-Bucket flow turns preferences into a transparent,
              repeatable ritual.
            </p>
          </div>
          <div className="case-shopper__steps reveal">
            <article>
              <span>01</span>
              <Sparkles aria-hidden="true" />
              <h3>Set the taste profile</h3>
              <p>Favourites, hard no's, adventure level and budget.</p>
            </article>
            <article>
              <span>02</span>
              <ShoppingBag aria-hidden="true" />
              <h3>Reveal the Bucket</h3>
              <p>Every item, price and recommendation reason stays visible.</p>
            </article>
            <article>
              <span>03</span>
              <CalendarDays aria-hidden="true" />
              <h3>Choose the rhythm</h3>
              <p>One-off or weekly, then a proposed scheduled local run.</p>
            </article>
          </div>
          <a className="case-action case-action--plum reveal" href="/experience">
            Open the shopper experience
            <ArrowRight aria-hidden="true" />
          </a>
        </section>

        <section
          className="case-pilot"
          id="four-week-pilot"
          data-case-section="pilot"
          aria-labelledby="case-pilot-title"
        >
          <div className="case-pilot__heading reveal">
            <span className="case-pilot__badge">
              <Check aria-hidden="true" />
              Proposed 4-week pilot
            </span>
            <h2 id="case-pilot-title">Small enough to stop. Real enough to learn.</h2>
            <p>
              Agree the inputs and decision rules before launch. The pilot
              tests route density, customer behaviour and operational load; it
              does not assume a successful result.
            </p>
          </div>

          <ol className="case-pilot__weeks reveal">
            <li>
              <span>Week 01</span>
              <strong>Validate the inputs</strong>
              <p>
                Confirm signed marketplace fees, aggregate order history,
                service area, loaded route cost and available capacity.
              </p>
            </li>
            <li>
              <span>Week 02</span>
              <strong>Prepare a capped offer</strong>
              <p>
                Set proposed zones, run days, cut-offs, customer fee, product
                availability and support rules.
              </p>
            </li>
            <li>
              <span>Week 03</span>
              <strong>Operate the planned runs</strong>
              <p>
                Record completed drops, route time, support contacts,
                substitutions, failed deliveries and weekly intent.
              </p>
            </li>
            <li>
              <span>Week 04</span>
              <strong>Make the decision</strong>
              <p>
                Compare contribution per order, route density, on-time
                performance and repeat demand with pre-agreed thresholds.
              </p>
            </li>
          </ol>

          <div className="case-pilot__inputs reveal">
            <div>
              <span>Inputs required from Sub Zero Treats</span>
              <ul>
                <li>Signed merchant fee and recent payout examples</li>
                <li>Eight weeks of aggregate orders by day, postcode and value</li>
                <li>Loaded driver, vehicle and dispatch cost</li>
                <li>Capacity, cut-off, service area and fee decisions</li>
              </ul>
            </div>
            <div>
              <span>Decision pack at the end</span>
              <ul>
                <li>Direct-versus-marketplace contribution range</li>
                <li>Observed route density and break-even threshold</li>
                <li>Customer one-off and weekly intent</li>
                <li>Proceed, adjust or stop recommendation</li>
              </ul>
            </div>
          </div>
        </section>

        <section
          className="case-proof"
          data-case-section="local-proof"
          aria-labelledby="case-proof-title"
        >
          <div className="case-section-heading reveal">
            <p className="case-kicker">Local digital implementation</p>
            <h2 id="case-proof-title">
              An operational test, not a speculative rebuild.
            </h2>
            <p>
              Penny Wise I.T is presenting this prototype to show how scheduled
              ordering, subscriber controls, owner alerts and delivery
              workflows can fit together for a local operator.
            </p>
          </div>

          <div className="case-proof__work reveal">
            <article>
              <span>Scheduled ordering</span>
              <h3>Windows with guardrails</h3>
              <p>
                Proposed service zones, cut-offs, capacity states and sold-out
                behaviour can be designed before the pilot accepts demand.
              </p>
            </article>
            <article>
              <span>Subscriber controls</span>
              <h3>Clear customer choices</h3>
              <p>
                Weekly intent, skip, pause and cancellation language stays
                explicit before any production subscription is introduced.
              </p>
            </article>
            <article>
              <span>Route operations</span>
              <h3>Known work for every run</h3>
              <p>
                Dispatch, owner alerts, delivery status and exception handling
                can be scoped from observed pilot needs.
              </p>
            </article>
          </div>

          <p className="case-proof__note reveal">
            No third-party client outcome is asserted on this page. Named
            portfolio work, dates and results should be published only with a
            public source or client permission.
          </p>
        </section>

        <section
          className="case-included"
          data-case-section="included-package"
          aria-labelledby="case-included-title"
        >
          <div className="case-included__texture" aria-hidden="true" />
          <div className="case-section-heading reveal">
            <p className="case-kicker">Included operating package</p>
            <h2 id="case-included-title">
              The website is only the front door. The support system comes
              with it.
            </h2>
            <p>
              The build is paired with practical tools to promote the offer
              and report problems without adding another messy owner workflow.
            </p>
          </div>

          <div className="case-included__row reveal">
            <article>
              <span>Core build</span>
              <h3>Direct-order experience</h3>
              <p>
                The customer journey shown in this MVP, shaped around Sub
                Zero&apos;s products, bucket builder and proposed delivery
                days.
              </p>
            </article>
            <article>
              <span>Included</span>
              <img
                className="case-included__partner-logo case-included__partner-logo--social"
                src="/partners/social-ai-studio-logo.webp"
                alt="Social AI Studio logo"
                width="954"
                height="396"
                loading="lazy"
                decoding="async"
              />
              <h3>Social AI Studio</h3>
              <p>
                Create Facebook and Instagram posts, campaign images and draft
                content calendars around new flavours, weekly drops and
                seasonal offers&mdash;using Sub Zero&apos;s brand profile as
                context.
              </p>
            </article>
            <article>
              <span>Included</span>
              <img
                className="case-included__partner-logo case-included__partner-logo--bug"
                src="/partners/bug-squasher-icon.svg"
                alt="Bug Squasher support app icon"
                width="512"
                height="512"
                loading="lazy"
                decoding="async"
              />
              <h3>Bug Squasher</h3>
              <p>
                Give Sub Zero staff a personalised support app for reporting
                website issues with screenshots, photos or video. Penny Wise
                can triage, acknowledge and track each concern without
                Facebook-message ping-pong.
              </p>
            </article>
          </div>

          <p className="case-included__scope reveal">
            These package inclusions do not change the illustrative delivery
            economics shown above. Included account setup, ongoing support
            terms and any third-party usage or media costs are confirmed in the
            final scope.
          </p>
        </section>

        <section
          className="case-close"
          data-case-section="close"
          aria-labelledby="case-close-title"
        >
          <p className="reveal">The decision is not "platforms or direct".</p>
          <h2 id="case-close-title" className="reveal">
            Approve one working session. Put the real inputs on the table.
          </h2>
          <p className="case-close__summary reveal">
            In 30 minutes, confirm the actual merchant fees, possible service
            zones, possible run days, available capacity and the decision rules
            for a capped pilot. No route or launch is approved by viewing this
            proposal.
          </p>
          <div className="reveal">
            <a
              className="case-action case-action--plum"
              href="mailto:?subject=Sub%20Zero%20Treats%20%E2%80%94%20pilot%20working%20session&body=Steve%2C%20I%27d%20like%20to%20book%20a%2030-minute%20working%20session%20to%20review%20the%20direct-delivery%20proposal%2C%20confirm%20the%20real%20inputs%20and%20agree%20the%20next%20step."
            >
              Prepare the approval email
              <Mail aria-hidden="true" />
            </a>
            <a className="case-action case-action--chalk" href="/">
              Return to the full proposal
              <ArrowLeft aria-hidden="true" />
            </a>
          </div>
          <small className="case-close__note reveal">
            This prepares a reply in your email app. Send it to Steve using the
            contact details that accompanied this private proposal.
          </small>
        </section>
      </main>

      <footer className="case-footer">
        <a className="case-logo" href="/" aria-label="Back to the full proposal">
          <img src="/brand/sub-zero-logo-primary.png" alt="Sub Zero Treats" />
        </a>
        <p>
          A Penny Wise I.T proposal built around illustrative assumptions,
          operational guardrails and a customer journey worth testing.
        </p>
        <a href="#top">Back to top</a>
      </footer>
    </div>
  )
}

export default DirectDeliveryCasePage
